// ==========================================
// FILE: src/components/features/MapView/MapPage.js
// NEW: Integrated search form drives both Map View and Dual View
// ==========================================
import React, { useState } from 'react';
import { MapPin, Search, AlertCircle, X } from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI';
import MapViewComponent from './MapViewComponent';
import DualViewComponent from './DualViewComponent';
import LocationPicker from '../Search/LocationPicker';

// ── Normalise posts so media_urls is always an array ─────────────────────────
const normalisePost = (post) => {
  if (Array.isArray(post.media_urls)) return post;
  if (post.media_url) return { ...post, media_urls: [post.media_url] };
  return { ...post, media_urls: [] };
};

const MapPage = ({ user }) => {
  const { apiCall } = useAPI();

  // ── view ──
  const [viewMode, setViewMode] = useState('map');

  // ── search state ──
  const [posts, setPosts]                   = useState([]);
  const [searching, setSearching]           = useState(false);
  const [hasSearched, setHasSearched]       = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // ── search form fields ──
  const [category, setCategory]     = useState('all');     // all | missing | found
  const [animalType, setAnimalType] = useState('');
  const [petName, setPetName]       = useState('');
  const [location, setLocation]     = useState(null);      // { name, latitude, longitude }
  const [radiusKm, setRadiusKm]     = useState(50);

  // Posts that have coordinates (required for map rendering)
  const geoPosts = posts.filter(p => p.latitude != null && p.longitude != null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setHasSearched(true);

    try {
      const searchData = {
        pet_name:    petName || '',
        animal_type: animalType || null,
        description: '',
        latitude:    location?.latitude  ?? null,
        longitude:   location?.longitude ?? null,
        location:    location?.name      ?? '',
        radius_km:   radiusKm,
        category:    category !== 'all' ? category : null,
      };

      const data = await apiCall('/search', {
        method: 'POST',
        body: JSON.stringify(searchData),
      });

      const results = (data.posts || []).map(normalisePost);
      setPosts(results);
    } catch (err) {
      console.error('Map search error:', err);
      alert(`Search failed: ${err.message}`);
      setPosts([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setPosts([]);
    setHasSearched(false);
    setPetName('');
    setAnimalType('');
    setCategory('all');
    setLocation(null);
    setRadiusKm(50);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">

      {/* ── Page header ── */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold flex items-center gap-2 mb-1">
          <MapPin size={28} />
          Pet Location Map
        </h2>
        <p className="text-sm opacity-90">Search for lost & found pets and see them on the map</p>
      </div>

      {/* ── Search panel ── */}
      <div className="bg-white rounded-xl shadow-lg p-5">
        <form onSubmit={handleSearch}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">

            {/* Category */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Post Type</label>
              <div className="flex gap-1 flex-wrap">
                {[
                  { value: 'all',     label: 'All' },
                  { value: 'missing', label: '🔴 Missing' },
                  { value: 'found',   label: '🟢 Found' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategory(opt.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      category === opt.value
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Pet name */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Pet Name</label>
              <input
                type="text"
                value={petName}
                onChange={e => setPetName(e.target.value)}
                placeholder="e.g. Bella"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            {/* Animal type */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Animal Type</label>
              <select
                value={animalType}
                onChange={e => setAnimalType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
              >
                <option value="">All Animals</option>
                <option value="dog">🐕 Dog</option>
                <option value="cat">🐈 Cat</option>
                <option value="bird">🦜 Bird</option>
                <option value="rabbit">🐰 Rabbit</option>
                <option value="other">🐾 Other</option>
              </select>
            </div>

            {/* Location + radius */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Location &amp; Radius
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowLocationPicker(true)}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition truncate ${
                    location
                      ? 'bg-green-50 border-green-400 text-green-800'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {location ? `📍 ${location.name}` : '🗺️ Pick location'}
                </button>
                {location && (
                  <button
                    type="button"
                    onClick={() => setLocation(null)}
                    className="text-gray-400 hover:text-red-500 transition"
                    title="Clear location"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {location && (
                <div className="mt-1">
                  <input
                    type="range"
                    min="5" max="200" step="5"
                    value={radiusKm}
                    onChange={e => setRadiusKm(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-blue-600 mt-0.5">Radius: {radiusKm} km</p>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={searching}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold text-sm disabled:opacity-50"
            >
              {searching ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Searching…
                </>
              ) : (
                <>
                  <Search size={16} />
                  Search &amp; Show on Map
                </>
              )}
            </button>

            {hasSearched && (
              <button
                type="button"
                onClick={clearSearch}
                className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
              >
                Clear
              </button>
            )}

            {hasSearched && (
              <p className="text-sm text-gray-500 ml-auto">
                {posts.length} result{posts.length !== 1 ? 's' : ''}
                {geoPosts.length < posts.length && (
                  <span className="ml-1 text-xs text-amber-600">
                    ({geoPosts.length} with location)
                  </span>
                )}
              </p>
            )}
          </div>
        </form>
      </div>

      {/* ── View toggle ── */}
      {hasSearched && (
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setViewMode('map')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                viewMode === 'map'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🗺️ Map View
            </button>
            <button
              onClick={() => setViewMode('dual')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                viewMode === 'dual'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ⚡ Dual View
            </button>
          </div>

          {/* No results */}
          {posts.length === 0 && !searching && (
            <div className="text-center py-16">
              <AlertCircle size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600 font-semibold text-lg">No results found</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your search criteria or expanding the radius.</p>
            </div>
          )}

          {/* No geo-tagged posts */}
          {posts.length > 0 && geoPosts.length === 0 && (
            <div className="text-center py-12 bg-amber-50 rounded-lg border border-amber-200">
              <MapPin size={40} className="mx-auto mb-3 text-amber-400" />
              <p className="text-amber-800 font-semibold">
                {posts.length} result{posts.length !== 1 ? 's' : ''} found, but none have location data
              </p>
              <p className="text-amber-600 text-sm mt-1">Only posts with a map pin can appear here.</p>
            </div>
          )}

          {/* Map / Dual view */}
          {geoPosts.length > 0 && (
            <>
              {viewMode === 'map'  && <MapViewComponent  posts={geoPosts} />}
              {viewMode === 'dual' && <DualViewComponent posts={geoPosts} />}
            </>
          )}
        </div>
      )}

      {/* Pre-search placeholder */}
      {!hasSearched && (
        <div className="bg-white rounded-xl shadow-lg p-16 text-center">
          <MapPin size={56} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg font-semibold">Use the search above to find pets</p>
          <p className="text-gray-400 text-sm mt-2">Results will appear on the map and in dual view.</p>
        </div>
      )}

      {/* Location picker modal */}
      {showLocationPicker && (
        <LocationPicker
          onLocationSelected={(loc) => { setLocation(loc); setShowLocationPicker(false); }}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </div>
  );
};

export default MapPage;